import { useState } from "react";
import {
  Badge,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@repo/ui";
import {
  ArrowRightLeft,
  Calendar,
  Check,
  ChevronDown,
  ChevronUp,
  ClipboardPaste,
  Clock,
  Mic,
  Type,
  User,
} from "lucide-react";
import { calculerScores, calculerStatsCheating, estSuspect, formatDateTime, formatDuration } from "@repo/utils";
import type { EntStructQuestionEtat, EntStructResultsProps } from "./entStruct.types";
import {
  calculerMoyenneCaracteres,
  calculerStatsTranscription,
  construireExchangesMap,
  getMessagesFromReponses,
} from "./entStruct.utils";
import {
  CompetenceCard,
  ConversationDropdown,
  MessageAucuneDonnee,
  ScoreGlobalHeader,
} from "./EntStructComponents";

export type { EntStructMessage, EntStructTranscriptionMeta, TranscriptionStats } from "./entStruct.types";
export { calculerMoyenneCaracteres, calculerStatsTranscription } from "./entStruct.utils";

export function EntStructResults({
  donnees,
  candidatInfo,
  resultatsRacine,
  notationsParModele,
}: EntStructResultsProps) {
  const [voirPlus, setVoirPlus] = useState(false);

  if (donnees.type !== "fairview") {
    return <div>Type de données incorrect</div>;
  }

  const resultatsAffichage = resultatsRacine;
  const questionsEtat = (() => {
    if (resultatsAffichage?.length) {
      try {
        const parsed = JSON.parse(resultatsAffichage.at(-1)!.data);
        if (parsed.result?.questions?.length)
          return parsed.result.questions as EntStructQuestionEtat[];
      } catch {}
    }
    return donnees.questionsEtat;
  })();

  if (!questionsEtat || questionsEtat.length === 0) {
    return <MessageAucuneDonnee />;
  }

  const scores = calculerScores(questionsEtat);
  const questionsMap = new Map(
    questionsEtat.map((q) => [q.questionId, q.question_text]),
  );
  // Échanges retenus par l'IA pour évaluer les critères de chaque question
  // (reconstruits depuis la transcription sur les évaluations sans `exchanges`).
  const exchangesMap = construireExchangesMap(questionsEtat, donnees.reponses);
  const messagesReponses = donnees.reponses
    ? getMessagesFromReponses(donnees.reponses)
    : [];
  const statsCheating = calculerStatsCheating(messagesReponses);
  const tricheDetectee = estSuspect(messagesReponses);

  const statsTranscription = donnees.reponses
    ? calculerStatsTranscription(donnees.reponses)
    : null;
  const statsCaracteres = donnees.reponses
    ? calculerMoyenneCaracteres(donnees.reponses)
    : null;

  const criteresApresRelance = scores.competences.reduce(
    (sum, c) =>
      sum +
      (c.criteres?.filter((cr) => cr.asked && cr.covered).length ??
        c.sousCompetences.reduce(
          (s, sc) =>
            s + sc.criteres.filter((cr) => cr.asked && cr.covered).length,
          0,
        )),
    0,
  );
  const criteresSpontanes = scores.competences.reduce(
    (sum, c) =>
      sum +
      (c.criteres?.filter((cr) => !cr.asked && cr.covered).length ??
        c.sousCompetences.reduce(
          (s, sc) =>
            s + sc.criteres.filter((cr) => !cr.asked && cr.covered).length,
          0,
        )),
    0,
  );

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6 p-4 md:p-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold">FairView</h1>
            <div className="mt-1 flex items-center gap-2 text-sm text-muted-foreground">
              <User className="h-4 w-4" />
              {candidatInfo.prenom} {candidatInfo.nom} — {candidatInfo.email}
            </div>
          </div>
          <Badge variant="success">Terminée</Badge>
        </div>
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          {candidatInfo.tempsPasse !== undefined && (
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <div>
                <span className="text-muted-foreground">Temps passé</span>
                <p className="font-medium">
                  {formatDuration(candidatInfo.tempsPasse)}
                </p>
              </div>
            </div>
          )}
          {candidatInfo.dateDebut && (
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <div>
                <span className="text-muted-foreground">Date de début</span>
                <p className="font-medium">
                  {formatDateTime(candidatInfo.dateDebut)}
                </p>
              </div>
            </div>
          )}
          {candidatInfo.dateFin && (
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <div>
                <span className="text-muted-foreground">Date de fin</span>
                <p className="font-medium">
                  {formatDateTime(candidatInfo.dateFin)}
                </p>
              </div>
            </div>
          )}
          {statsTranscription && statsTranscription.pourcentageGlobal > 50 && (
            <span
              className="flex items-center gap-2 cursor-default"
              title={`${statsTranscription.pourcentageGlobal}% du contenu transcrit`}
            >
              <Mic className="h-4 w-4 text-etat-info pointer-events-none" />
              <span className="text-etat-info font-medium">
                Transcription utilisée
              </span>
            </span>
          )}
        </div>

        {voirPlus && (
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div className="flex items-center gap-2">
              <Type className="h-4 w-4 text-muted-foreground" />
              <div>
                <span className="text-muted-foreground">
                  Taille moyenne d'une réponse
                </span>
                <p className="font-medium">
                  {statsCaracteres?.moyenne} caractères
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <ArrowRightLeft className="h-4 w-4 text-etat-info" />
              <div>
                <span className="text-muted-foreground">
                  Critères couverts après relance
                </span>
                <p className="font-medium text-etat-info">
                  {criteresApresRelance}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Check className="h-4 w-4 text-etat-fait" />
              <div>
                <span className="text-muted-foreground">
                  Critères couverts spontanément
                </span>
                <p className="font-medium text-etat-fait">
                  {criteresSpontanes}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <ClipboardPaste
                className={`h-4 w-4 ${statsCheating.nombrePastes > 0 ? "text-etat-erreur" : "text-muted-foreground"}`}
              />
              <div>
                <span className="text-muted-foreground">
                  Copier-coller détectés
                </span>
                <p
                  className={`font-medium ${statsCheating.nombrePastes > 0 ? "text-etat-erreur" : ""}`}
                >
                  {statsCheating.nombrePastes}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="mt-3 flex justify-center">
          <button
            type="button"
            onClick={() => setVoirPlus(!voirPlus)}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1"
          >
            Voir plus
            {voirPlus ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </button>
        </div>
      </div>

      <Card className="mb-6 w-full overflow-hidden">
        <CardHeader>
          <CardTitle>Résultats bruts</CardTitle>
          <CardDescription>Données brutes de l'évaluation</CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <div className="space-y-6 w-full max-w-full min-w-0">
            <ScoreGlobalHeader
              obtenu={scores.scoreGlobalObtenu}
              max={scores.scoreGlobalMax}
              brutObtenu={scores.scoreGlobalBrut}
              brutMax={scores.scoreGlobalMaxBrut}
              tricheDetectee={tricheDetectee}
              statsCheating={statsCheating}
            />

            <div className="space-y-3">
              <h3 className="text-lg font-semibold">Détail par compétence</h3>
              {scores.competences.map((competence, index) => (
                <CompetenceCard
                  key={index}
                  competence={competence}
                  isHierarchical={scores.isHierarchical}
                  questionsMap={questionsMap}
                  exchangesMap={exchangesMap}
                  notationsParModele={notationsParModele}
                />
              ))}
            </div>

            {donnees.reponses && Object.keys(donnees.reponses).length > 0 && (
              <div className="space-y-3">
                <h3 className="text-lg font-semibold">Transcription</h3>
                <ConversationDropdown reponses={donnees.reponses} />
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
